import { cbamCnCodeKey, normalizeCnCode } from './formatCnCode';

// A full CN code is at least 6 digits (HS6); anything shorter in a
// whitespace-split chunk is one group of a spaced code ("7208 10 00").
const MIN_CODE_DIGITS = 6;

/**
 * Split pasted text into CN codes, digits only, deduped, in paste order.
 *
 * Accepts what people actually paste: an Excel column (newlines), a CSV/TSV
 * row, a comma/semicolon list, and codes in any delimiter style
 * ("7208 10 00", "7208.10.00", "72081000"). A cell that carries a label
 * ("7208 10 00 - Flat-rolled …") keeps only its leading code. Whitespace
 * separates codes only when every whitespace token is a full code, so
 * "72081000 72082500" is two codes but "7208 10 00" is one.
 *
 * @param {string} text
 * @returns {string[]} normalized codes (digits only)
 */
export function parseCnCodeList(text) {
  if (typeof text !== 'string') return [];
  const codes = [];
  for (const cell of text.split(/[\r\n\t,;]+/)) {
    const lead = cell.trim().match(/^\d[\d\s.-]*/)?.[0] ?? '';
    const tokens = lead.trim().split(/\s+/).filter(Boolean);
    const separate =
      tokens.length > 1 && tokens.every((t) => normalizeCnCode(t).length >= MIN_CODE_DIGITS);
    for (const raw of separate ? tokens : [lead]) {
      const code = normalizeCnCode(raw);
      if (/^\d+$/.test(code) && !codes.includes(code)) codes.push(code);
    }
  }
  return codes;
}

/**
 * Match pasted codes against select options keyed by CN code. A TARIC code
 * (10+ digits) matches its CN8 catalogue entry.
 *
 * @param {string[]} codes - from parseCnCodeList
 * @param {Array<{value: string}>} options
 * @returns {{ matched: Array, unmatched: string[] }}
 */
export function matchCnCodeOptions(codes, options) {
  const byKey = new Map();
  for (const option of options || []) {
    const key = normalizeCnCode(option?.value);
    if (key && !byKey.has(key)) byKey.set(key, option);
  }
  const matched = [];
  const unmatched = [];
  for (const code of codes) {
    const option = byKey.get(code) ?? byKey.get(cbamCnCodeKey(code));
    if (!option) unmatched.push(code);
    else if (!matched.includes(option)) matched.push(option);
  }
  return { matched, unmatched };
}
